import { useState, useCallback } from 'react'
import { Animated, Platform } from 'react-native'
import { transitions } from '@adalo/constants'
import { TRANSITION_CONFIG } from 'utils/transitions'
import { getScreenSize } from 'utils/responsive'

export const SLIDE_TRANSITIONS = [
  transitions.SLIDE_LEFT,
  transitions.SLIDE_RIGHT,
  transitions.SLIDE_UP,
  transitions.SLIDE_DOWN,
]

export const FLOAT_TRANSITIONS = [
  transitions.FLOAT_LEFT,
  transitions.FLOAT_RIGHT,
  transitions.FLOAT_UP,
  transitions.FLOAT_DOWN,
]

const DEFAULT_TRANSITION_TIMING = 300

export const useTransitionAnimations = () => {
  // Create animated value for view offset
  const [currentViewOffset] = useState(() => new Animated.Value(0))

  const isAndroid = useCallback(() => {
    const result = Platform.OS === 'android'

    return result
  }, [])

  const getTiming = useCallback(transitionType => {
    let timing = DEFAULT_TRANSITION_TIMING
    if (transitionType === transitions.NONE) {
      timing = 0
    } else if (FLOAT_TRANSITIONS.includes(transitionType)) {
      timing = DEFAULT_TRANSITION_TIMING - 50 // Slightly faster for float transitions
    }

    return timing
  }, [])

  const getAnimationConfig = useCallback(
    transition => {
      let {
        transformType,
        transformDirection,
        underViewMultiple,
        dimUnderView,
        gap,
      } = TRANSITION_CONFIG[transition]

      gap *= transformDirection

      let transformDistance =
        transformType === 'translateX'
          ? getScreenSize().width
          : getScreenSize().height
      transformDistance *= transformDirection

      const isAndroidPlatform = isAndroid()

      return {
        transformType,
        transformDirection,
        underViewMultiple,
        dimUnderView,
        gap,
        transformDistance,
        isAndroidPlatform,
      }
    },
    [isAndroid]
  )

  const getAnimationStyles = useCallback(
    (transition, baseOffset, animationConfig) => {
      const inputRange = [baseOffset, baseOffset + 1]

      // Handle Android-specific fade styles
      let fadeStyles = {}
      let finalTransformType = animationConfig.transformType
      let finalTransformDistance = animationConfig.transformDistance

      if (
        animationConfig.isAndroidPlatform &&
        [transitions.MODAL, transitions.PUSH].indexOf(transition) !== -1
      ) {
        fadeStyles = {
          opacity: currentViewOffset.interpolate({
            inputRange,
            outputRange: [0, 1],
          }),
        }

        finalTransformDistance = getScreenSize().height // Use full screen height for Android transitions
        finalTransformType = 'translateY'
      }

      // Determine transition types
      const isFloatTransition = FLOAT_TRANSITIONS.includes(transition)
      const isSlideTransition = SLIDE_TRANSITIONS.includes(transition)
      const isModalTransition = transition === transitions.MODAL

      // Helper function to get screen animation config based on transition type
      const getScreenConfig = isCurrentScreen => {
        if (isFloatTransition) {
          return {
            transform: isCurrentScreen
              ? finalTransformDistance + animationConfig.gap
              : 0,
            opacity: isCurrentScreen ? [0, 1] : [1, 0.7],
            scale: isCurrentScreen ? [0.95, 1] : [1, 0.95],
          }
        } else if (isSlideTransition) {
          return {
            transform: isCurrentScreen
              ? finalTransformDistance + animationConfig.gap
              : -animationConfig.underViewMultiple * finalTransformDistance -
                animationConfig.gap,
            opacity: [1, 1],
            scale: [1, 1],
          }
        } else if (isModalTransition) {
          return {
            transform: isCurrentScreen ? getScreenSize().height : 0,
            opacity: isCurrentScreen ? [0.5, 1] : [1, 0.5],
            scale: isCurrentScreen ? [0.98, 1] : [1, 0.98],
          }
        } else {
          // Default push transition
          return {
            transform: isCurrentScreen
              ? finalTransformDistance + animationConfig.gap
              : -animationConfig.underViewMultiple * finalTransformDistance -
                animationConfig.gap,
            opacity: [1, 1],
            scale: [1, 1],
          }
        }
      }

      const previousConfig = getScreenConfig(false)
      const currentConfig = getScreenConfig(true)

      const previousScreenAnimationStyles = [
        {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
        {
          transform: [
            {
              [finalTransformType]: currentViewOffset.interpolate({
                inputRange,
                outputRange: [0, previousConfig.transform],
                extrapolate: 'clamp',
              }),
            },
            {
              scale: currentViewOffset.interpolate({
                inputRange,
                outputRange: previousConfig.scale,
                extrapolate: 'clamp',
              }),
            },
          ],
          opacity: currentViewOffset.interpolate({
            inputRange,
            outputRange: previousConfig.opacity,
            extrapolate: 'clamp',
          }),
        },
      ]

      const currentScreenAnimationStyles = [
        {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
        {
          ...fadeStyles,
          transform: [
            {
              [finalTransformType]: currentViewOffset.interpolate({
                inputRange,
                outputRange: [currentConfig.transform, 0],
                extrapolate: 'clamp',
              }),
            },
            {
              scale: currentViewOffset.interpolate({
                inputRange,
                outputRange: currentConfig.scale,
                extrapolate: 'clamp',
              }),
            },
          ],
          opacity: currentViewOffset.interpolate({
            inputRange,
            outputRange: currentConfig.opacity,
            extrapolate: 'clamp',
          }),
        },
      ]

      return {
        previousScreenAnimationStyles,
        currentScreenAnimationStyles,
        fadeStyles,
        finalTransformType,
        finalTransformDistance,
        inputRange,
      }
    },
    [currentViewOffset]
  )

  return {
    currentViewOffset,
    getTiming,
    getAnimationConfig,
    getAnimationStyles,
    isAndroid,
  }
}
